import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import MeetingsSection from '../../components/MeetingsSection';
import BabyBenefitsSection from '../../components/BabyBenefitsSection';
import CourseInclusionsSection from '../../components/CourseInclusionsSection';
import FaqSection from '../../components/FaqSection';
import { CloseX, ArrowRightThin } from '../../components/vd/LineArt';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');

  const closeModal = () => {
    setForm({
      name: '',
      email: '',
      message: '',
    });
    setState('idle');
    setError('');
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    const email = form.email.trim();
    const name = form.name.trim();
    const message = form.message.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Vnesi veljaven e-mail.');
      return;
    }

    if (message.length < 3) {
      setError('Napiši kratko sporočilo.');
      return;
    }

    setState('loading');

    try {
      const r = await fetch(`${BACKEND}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          name,
          message,
          program: 'iaim',
          source: 'iaim-page',
        }),
      });

      if (!r.ok) {
        throw new Error('fail');
      }

      setState('success');
    } catch {
      setState('error');
      setError('Nekaj je šlo narobe. Poskusi znova.');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid="iaim-booking-modal"
    >
      <div
        className="absolute inset-0 bg-vd-text/40 backdrop-blur-sm"
        onClick={closeModal}
      />

      <div className="relative bg-vd-bg rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 border border-beige-200 vd-fade">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 text-vd-muted hover:text-vd-text p-2"
          aria-label="Zapri"
          data-testid="iaim-booking-close"
        >
          <CloseX size={18} />
        </button>

        {state === 'success' ? (
          <div>
            <h3 className="font-serif text-2xl text-beige-800 mb-2">
              Hvala. Tvoje povpraševanje je bilo poslano.
            </h3>

            <p className="text-vd-text/85 leading-relaxed">
              Sporočilo sem prejela in se ti glede prostega termina oglasim
              osebno na vpisani e-poštni naslov.
            </p>
          </div>
        ) : (
          <>
            <h3 className="font-serif text-2xl text-beige-800 mb-2">
              Rezerviraj srečanje za IAIM masažo
            </h3>

            <p className="text-sm text-vd-muted mb-4">
              Vnesi svoj e-mail in kratko sporočilo. Odpisala ti bom osebno.
            </p>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-xs text-vd-muted mb-1">
                  Ime (neobvezno)
                </label>

                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                    })
                  }
                  className="w-full bg-white border border-beige-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-beige-700"
                  data-testid="iaim-booking-name"
                />
              </div>

              <div>
                <label className="block text-xs text-vd-muted mb-1">
                  E-mail *
                </label>

                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      email: e.target.value,
                    })
                  }
                  className="w-full bg-white border border-beige-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-beige-700"
                  data-testid="iaim-booking-email"
                />
              </div>

              <div>
                <label className="block text-xs text-vd-muted mb-1">
                  Sporočilo *
                </label>

                <textarea
                  required
                  rows="4"
                  value={form.message}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      message: e.target.value,
                    })
                  }
                  className="w-full bg-white border border-beige-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-beige-700 resize-none"
                  data-testid="iaim-booking-message"
                  placeholder="Kdaj bi radi prišli? Kaj vas najbolj zanima?"
                />
              </div>

              {error && (
                <p className="text-xs text-red-700">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={state === 'loading'}
                className="w-full inline-flex items-center justify-center gap-2 bg-beige-700 hover:bg-beige-800 text-white rounded-full py-3 text-sm font-medium disabled:opacity-50 transition-all"
                data-testid="iaim-booking-submit"
              >
                {state === 'loading' ? 'Pošiljam …' : 'Pošlji sporočilo'}

                {state !== 'loading' && (
                  <ArrowRightThin size={14} />
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const MasazaDojenckaPage = () => {
  const [open, setOpen] = useState(false);

  return (
    <main data-testid="page-program-masaza">
      <Helmet>
        <title>Tečaj masaže dojenčka IAIM – Val Dotika</title>
        <meta
          name="description"
          content="Tečaj masaže dojenčka po IAIM metodi – Špela Ferbežar Ban."
        />
      </Helmet>

      <MeetingsSection />
      <BabyBenefitsSection />
      <CourseInclusionsSection />
      <FaqSection />

      <section
        className="pt-4 pb-14 lg:pb-20 text-center"
        data-testid="iaim-booking-section"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-beige-700 hover:bg-beige-800 text-white rounded-full px-6 py-3 text-sm font-medium transition-all"
            data-testid="iaim-cta"
          >
            Rezerviraj srečanje za IAIM masažo
            <ArrowRightThin size={14} />
          </button>
        </div>
      </section>

      <BookingModal
        open={open}
        onClose={() => setOpen(false)}
      />
    </main>
  );
};

export default MasazaDojenckaPage;